import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { service } from '@ember/service';
import Chip from 'tio-ui/components/chip';
import type { ChipIntent } from 'tio-ui/components/chip';
import { Section } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import { PhCheckCircle, PhCircle, PhWarning } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';

interface EmploymentPeriod {
  employerName: string;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
  status: EmploymentStatus;
}

type EmploymentStatus = 'verified' | 'pending' | 'in_progress' | 'requires_verification';

const STATUS_PRIORITY: Record<EmploymentStatus, number> = {
  in_progress: 0,
  requires_verification: 1,
  pending: 2,
  verified: 3,
};

interface S {
  Args: {
    forms: PslfFormModel[];
  };
  Element: HTMLElement;
}

function chipIntentForStatus(status: EmploymentStatus): ChipIntent {
  switch (status) {
    case 'verified':
      return 'success';
    case 'pending':
      return 'warning';
    case 'requires_verification':
      return 'warning';
    case 'in_progress':
      return 'default';
  }
}

function statusForPriorForm(form: PslfFormModel): EmploymentStatus {
  if (form.isSubmitted) {
    return 'verified';
  }
  return 'in_progress';
}

export default class PslfQualifyingEmployment extends Component<S> {
  @service declare intl: IntlService;

  get employmentPeriods(): EmploymentPeriod[] {
    const currentForms = this.args.forms.filter((f) => f.isCurrentEmployer);
    const priorForms = this.args.forms.filter((f) => !f.isCurrentEmployer);

    const periods: EmploymentPeriod[] = [];

    // Collapse all current employer forms into a single entry
    if (currentForms.length > 0) {
      // Use the most recent current form for display data
      // Compare parsed instants, not strings — serialized offsets flip across
      // DST (-05:00/-06:00) within one list, breaking lexicographic order.
      const latestCurrent = currentForms.sort(
        (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      )[0]!;

      const employerName = latestCurrent.employerDisplayName || 'Unknown Employer';
      const startDate = latestCurrent.employmentStartDate;

      // Only the freshest submission decides recertification. Asking whether *any* form is
      // expiring latches the warning on for every borrower who has ever recertified.
      const latestSubmission = currentForms
        .filter((f) => f.isSubmittedOrPending)
        .sort((a, b) => a.daysSinceSubmitted - b.daysSinceSubmitted)[0];

      // Any form not rejected/cancelled/expired/abandoned counts as active (in progress)
      const hasActiveForm = currentForms.some((form) => !form.isInactiveStatus);

      let currentStatus: EmploymentStatus = 'requires_verification';
      if (latestSubmission && !latestSubmission.isExpiring) {
        currentStatus = latestSubmission.isRecentlySubmitted ? 'verified' : 'in_progress';
      } else if (!latestSubmission && hasActiveForm) {
        currentStatus = 'in_progress';
      }

      periods.push({
        employerName,
        startDate,
        endDate: null,
        isCurrent: true,
        status: currentStatus,
      });
    }

    // Deduplicate prior employer forms by employer name + start date
    const priorMap = new Map<string, EmploymentPeriod>();
    for (const form of priorForms) {
      const employerName = form.employerDisplayName || 'Unknown Employer';
      const startDate = form.employmentStartDate;
      const endDate = form.employmentEndDate;
      const status = statusForPriorForm(form);

      const key = `${employerName.toLowerCase()}|${startDate ?? ''}`;
      const existing = priorMap.get(key);

      if (!existing || STATUS_PRIORITY[status] > STATUS_PRIORITY[existing.status]) {
        priorMap.set(key, { employerName, startDate, endDate, isCurrent: false, status });
      }
    }

    // Sort prior periods by start date descending
    const sortedPrior = [...priorMap.values()].sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return b.startDate.localeCompare(a.startDate);
    });

    return [...periods, ...sortedPrior];
  }

  statusLabel = (status: EmploymentStatus): string => {
    switch (status) {
      case 'verified':
        return this.intl.t('pslf.reimagined.qualifying_employment.status.verified');
      case 'pending':
        return this.intl.t('pslf.reimagined.qualifying_employment.status.pending');
      case 'in_progress':
        return this.intl.t('pslf.reimagined.qualifying_employment.status.in_progress');
      case 'requires_verification':
        return this.intl.t('pslf.reimagined.qualifying_employment.status.requires_verification');
    }
  };

  <template>
    <Section data-test-pslf-qualifying-employment ...attributes>
      <:header>
        {{t "pslf.reimagined.qualifying_employment.heading"}}
      </:header>
      <:body>
        <div class="space-y-4">
          {{#each this.employmentPeriods as |period|}}
            <div class="tio-row-top-tight">
              {{#if (isVerified period.status)}}
                <PhCheckCircle @size={{20}} @weight="fill" class="shrink-0 mt-0.5 tio-fg-info" />
              {{else if (isRequiresVerification period.status)}}
                <PhWarning @size={{20}} @weight="fill" class="shrink-0 mt-0.5 tio-fg-warning" />
              {{else}}
                <PhCircle @size={{20}} @weight="fill" class="shrink-0 mt-0.5 tio-fg-disabled" />
              {{/if}}
              <div class="tio-col-extra-tight">
                <div class="tio-row-tight flex-wrap">
                  <Text @weight="semibold">
                    {{period.employerName}}
                  </Text>
                  <Chip @intent={{chipIntentForStatus period.status}} @size="sm">
                    {{this.statusLabel period.status}}
                  </Chip>
                </div>
                <Text @variant="muted" @size="sm">
                  {{#if period.startDate}}
                    {{safeFormatDate period.startDate month="short" year="numeric"}}
                  {{else}}
                    —
                  {{/if}}
                  -
                  {{#if period.isCurrent}}
                    {{t "pslf.reimagined.qualifying_employment.current"}}
                  {{else if period.endDate}}
                    {{safeFormatDate period.endDate month="short" year="numeric"}}
                  {{else}}
                    —
                  {{/if}}
                </Text>
              </div>
            </div>
          {{/each}}

          <Text @variant="muted">
            {{t "pslf.reimagined.qualifying_employment.description"}}
          </Text>
        </div>
      </:body>
    </Section>
  </template>
}

function isVerified(status: EmploymentStatus): boolean {
  return status === 'verified';
}

function isRequiresVerification(status: EmploymentStatus): boolean {
  return status === 'requires_verification';
}
